import { state, shortUUID } from '@codesuma/baseline'
import { IDayCell } from './calendar'
import { Reminder } from './occasion-repeat'
import { nextSeq, sortBySeq } from './storage/rows'
import { collection } from './storage'

// The day's to-dos, persisted through the storage repository and broadcast
// through the baseline state bus: subscribe with state.on('tasks', handler)
// to redraw.
//
// A task belongs to one day and never repeats — that is the whole difference
// from a custom occasion, and why the Gregorian date alone places it. Creating
// one takes a title and nothing else; the reminder is set afterwards, from the
// row's own options.

export interface ITask {
    id: string
    title: string
    done: boolean
    reminder: Reminder
    // 'HH:MM' when the user gave the task an hour, absent when it belongs to
    // the day as a whole. A task with one has its reminder counted back from
    // that hour rather than from the day's nine o'clock — see occurrences.ts.
    time?: string
    gy: number
    gm: number
    gd: number
    // Insertion order. IndexedDB returns key order and shortUUID ids are
    // random, so add() stamps this and init() sorts by it. Optional so rows
    // saved before it existed keep the engine's load order.
    seq?: number
}

const KEY = 'tasks'
const repo = collection<ITask>(KEY)
let all: ITask[] = []

export async function init() {
    all = sortBySeq(await repo.load())
}

// In the order they were added (seq, then the engine's load order for
// legacy rows), so a list the user has built up does not reshuffle itself
// when something is ticked off.
function forDay(cell: IDayCell): ITask[] {
    return all.filter(t => t.gy === cell.gy && t.gm === cell.gm && t.gd === cell.gd)
}

function byId(id: string): ITask | undefined {
    return all.find(t => t.id === id)
}

function broadcast() {
    state.set(KEY, all)
}

// Everything the wizard collects, against the day it was opened on.
function add(cell: IDayCell, draft: { title: string, time?: string, reminder: Reminder }): ITask {
    const task: ITask = {
        id: shortUUID(),
        title: draft.title,
        done: false,
        reminder: draft.reminder,
        time: draft.time || undefined,
        gy: cell.gy,
        gm: cell.gm,
        gd: cell.gd,
        seq: nextSeq(all),
    }
    all = [...all, task]
    void repo.put(task)
    broadcast()
    return task
}

function update(id: string, patch: Partial<Pick<ITask, 'title' | 'done' | 'reminder' | 'time'>>) {
    let saved: ITask | undefined
    all = all.map(t => {
        if (t.id !== id) return t
        saved = { ...t, ...patch }
        return saved
    })
    if (saved) void repo.put(saved)
    broadcast()
}

// Putting a task off is moving it: it belongs to one day, so it leaves this
// day's list and joins another's. Its own fields are untouched, and the
// reminder follows on its own — the scheduler resyncs on every broadcast.
//
// Apart from update() because a date is not one of the task's answers: the
// wizard never asks for it twice, and only the row's options move a task.
function moveTo(id: string, cell: { gy: number, gm: number, gd: number }) {
    let saved: ITask | undefined
    all = all.map(t => {
        if (t.id !== id) return t
        saved = { ...t, gy: cell.gy, gm: cell.gm, gd: cell.gd }
        return saved
    })
    if (saved) void repo.put(saved)
    broadcast()
}

function remove(id: string) {
    all = all.filter(t => t.id !== id)
    void repo.remove(id)
    broadcast()
}

export default { init, forDay, byId, all: () => all, add, update, moveTo, remove }
